import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AssessmentBuilder = () => {
  const [assessmentType, setAssessmentType] = useState('multiple-choice');
  const [questions, setQuestions] = useState([]);

  const questionTypes = [
    { id: 'multiple-choice', label: 'Multiple Choice', icon: 'CheckSquare' },
    { id: 'true-false', label: 'True/False', icon: 'ToggleLeft' },
    { id: 'scenario', label: 'Scenario-Based', icon: 'FileText' },
    { id: 'practical', label: 'Practical Skill', icon: 'Tool' },
    { id: 'multimedia', label: 'Multimedia', icon: 'Image' }
  ];

  const handleAddQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      type: assessmentType,
      question: '',
      options: assessmentType === 'multiple-choice' ? ['', '', '', ''] : [],
      correctAnswer: '',
      points: 1
    };
    setQuestions([...questions, newQuestion]);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Panel - Assessment Settings */}
      <div className="lg:col-span-1 space-y-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Assessment Settings</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Assessment Title
              </label>
              <input
                type="text"
                placeholder="Enter assessment title"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Assessment Type
              </label>
              <select className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                <option>Mandatory Training</option>
                <option>Skill-Based</option>
                <option>Certification</option>
                <option>Competency Assessment</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Duration (minutes)
              </label>
              <input
                type="number"
                placeholder="30"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Passing Score (%)
              </label>
              <input
                type="number"
                placeholder="80"
                min="0"
                max="100"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Max Attempts
              </label>
              <input
                type="number"
                placeholder="3"
                min="1"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Enable Proctoring</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Randomize Questions</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Question Type Selector */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Question Types</h3>
          <div className="space-y-2">
            {questionTypes?.map((type) => (
              <button
                key={type?.id}
                onClick={() => setAssessmentType(type?.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  assessmentType === type?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-muted'
                }`}
              >
                <Icon name={type?.icon} size={20} />
                <span className="font-medium">{type?.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Right Panel - Question Builder */}
      <div className="lg:col-span-2">
        <div className="bg-card border border-border rounded-lg">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Questions</h3>
              <button
                onClick={handleAddQuestion}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
              >
                <Icon name="Plus" size={18} />
                Add Question
              </button>
            </div>
          </div>

          <div className="p-6">
            {questions?.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="HelpCircle" size={32} color="var(--color-muted-foreground)" />
                </div>
                <h4 className="text-lg font-semibold text-foreground mb-2">No Questions Added</h4>
                <p className="text-muted-foreground mb-4">
                  Start building your assessment by adding questions
                </p>
                <button
                  onClick={handleAddQuestion}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
                >
                  <Icon name="Plus" size={18} />
                  Add First Question
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {questions?.map((question, index) => (
                  <div key={question?.id} className="bg-background border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-foreground">Q{index + 1}</span>
                        <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                          {question?.type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="p-2 hover:bg-muted rounded-lg transition-colors">
                          <Icon name="Copy" size={16} />
                        </button>
                        <button className="p-2 hover:bg-muted rounded-lg transition-colors text-destructive">
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </div>

                    <textarea
                      placeholder="Enter your question here..."
                      className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary mb-4"
                      rows="3"
                    />

                    {question?.type === 'multiple-choice' && (
                      <div className="space-y-2">
                        {question?.options?.map((_, optIndex) => (
                          <div key={optIndex} className="flex items-center gap-3">
                            <input
                              type="radio"
                              name={`correct-${question?.id}`}
                              className="w-4 h-4"
                            />
                            <input
                              type="text"
                              placeholder={`Option ${optIndex + 1}`}
                              className="flex-1 px-3 py-2 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-foreground">Points:</label>
                        <input
                          type="number"
                          value={question?.points}
                          min="1"
                          className="w-20 px-2 py-1 bg-card border border-border rounded text-sm"
                        />
                      </div>
                      <button className="text-sm text-primary hover:underline">
                        Add Explanation
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {questions?.length > 0 && (
            <div className="p-6 border-t border-border bg-muted/50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Total Questions: <span className="font-semibold text-foreground">{questions?.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <button className="px-4 py-2 border border-border rounded-lg hover:bg-background transition-colors font-medium">
                    Save as Draft
                  </button>
                  <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium">
                    Publish Assessment
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentBuilder;